<template>
    <ModalBase
        name="cambiar-talla"
        :title="'Cambiar talla de la indumentaria ' + indumentaria.nombre"
        @close="close"
    >
        <div class="tw:space-y-4">
            <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                <div>
                    <BaseMultiselect
                        v-if="tallas"
                        label="Talla"
                        v-model="tallaSelect"
                        :options="tallas"
                        labelKey="nombre"
                        valueKey="id"
                    />
                    <p v-else class="tw:text-sm tw:text-gray-400">Cargando tallas...</p>
                </div>

                <div>
                    <BaseMultiselect
                        v-if="stockDisponible.length"
                        v-model="stockSelect"
                        name="stock"
                        label="Stock"
                        value-key="id"
                        label-key="name"
                        :options="stockOptions"
                    />
                    <p v-else class="tw:text-sm tw:text-gray-400">
                        {{ tallaSelect ? 'No hay unidades disponibles' : 'Selecciona una talla primero' }}
                    </p>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="tw:flex tw:gap-2 tw:justify-end">
                <Button variant="border" @click="close">
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    :disabled="!stockSelect && !selectedTallaObj"
                    @click="editarTalla"
                >
                    Continuar
                </Button>
            </div>
        </template>
    </ModalBase>
</template>

<script setup>
    import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue'
    import store from '@/store'
    import { config } from '@/config.js'
    import ModalBase from '@/modules/admin/ui/ModalBase.vue'
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue'
    import Button from '@/modules/admin/ui/Button.vue'

    const props = defineProps({
        indumentaria: {
            type: Object,
            required: true
        },
        listaId: {
            type: [String, Number],
            required: true
        },
        baseId: {
            type: [String, Number],
            required: true
        }
    })

    const emit = defineEmits(['close', 'talla-cambiada'])
    const instance = getCurrentInstance()

    const tallas = ref(null)
    const stock = ref(null)
    const stockDisponible = ref([])
    const tallaSelect = ref(null)
    const selectedTallaObj = ref(null)
    const stockSelect = ref(null)

    const stockOptions = computed(() =>
        stockDisponible.value.map(n => ({ id: n, name: String(n) }))
    )

    const close = () => {
        tallaSelect.value = null
        selectedTallaObj.value = null
        stockSelect.value = null
        stockDisponible.value = []
        instance.proxy.$modal.hide('cambiar-talla')
        emit('close')
    }

    const getTallas = async() => {
        const params = {
            lista_id: props.listaId,
            indumentaria_id: props.indumentaria.indumentaria_id,
            filters: {}
        }

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.indumentarias.get-tallas-indumentaria'),
                params
            )
            tallas.value = res.data.tallas

            // Remove current talla from options
            const currentTallaId = props.indumentaria.pivot?.talla_id
            if (currentTallaId && tallas.value) {
                const idx = tallas.value.findIndex(t => t.id === currentTallaId)
                if (idx !== -1) {
                    tallas.value.splice(idx, 1)
                }
            }
        } catch (error) {
            console.error('Error loading tallas:', error)
        }
    }

    const getStock = async talla => {
        const params = {
            lista_id: props.listaId,
            indumentaria_id: props.indumentaria.indumentaria_id,
            talla_id: talla.id,
            filters: {
                base_id: props.baseId
            }
        }

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.indumentarias.get-stock-talla'),
                params
            )
            stock.value = res.data.stock
        } catch (error) {
            console.error('Error loading stock:', error)
            stock.value = null
        }

        const cantidad = props.indumentaria.pivot?.cantidad || 1
        const maxStock = stock.value && stock.value <= cantidad ? stock.value : cantidad

        stockDisponible.value = []
        for (let i = 1; i <= maxStock; i++) {
            stockDisponible.value.push(i)
        }

        stockSelect.value = null
    }

    // BaseMultiselect emite solo el ID vía v-model.
    // Este watch recupera el objeto talla completo y pide el stock.
    watch(tallaSelect, newId => {
        if (newId == null || !tallas.value) return
        const talla = tallas.value.find(t => t.id === newId)
        if (!talla) return
        selectedTallaObj.value = talla
        getStock(talla)
    })

    const editarTalla = () => {
        emit('talla-cambiada', { talla: selectedTallaObj.value, stock: stockSelect.value })
        close()
    }

    onMounted(() => {
        getTallas()
    })
</script>
